import { Link, useLocation } from 'react-router-dom';
import { CircuitBoard, UserRoundPlus, X } from 'lucide-react';
import { useStrings } from '../locales';
import type { Strings } from '../locales';
import { useVoidNew } from '../hooks/useVoidNew';
import { WHATS_NEW_ITEMS } from '../content/whatsNew';

const NEW_PATHS_SET = new Set(WHATS_NEW_ITEMS.map((i) => i.path));

export interface NavItem {
  path: string;
  key: keyof Strings['nav']['items'];
  sub?: boolean;
  /** Tools that are not rulebook reference (Kohd Writer) get their own tech look. */
  tech?: boolean;
  /** Rulebook-style tools (Character Creation) get the same framed item with an icon, in the site's gold. */
  featured?: boolean;
  /** A tool still being tested carries a Beta tag in the sidebar, on Home and on its page. */
  beta?: boolean;
}

interface NavGroup {
  key: keyof Strings['nav']['groups'];
  items: NavItem[];
}

/** The site map: the sidebar draws it and the home page lists it. */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'gettingStarted',
    items: [
      { path: '/', key: 'home' },
      { path: '/introduction', key: 'introduction' },
      { path: '/character-creation', key: 'characterCreation', featured: true, beta: true },
    ],
  },
  {
    key: 'coreMaterials',
    items: [
      { path: '/classes',            key: 'classes' },
      { path: '/ancestries',         key: 'ancestries' },
      { path: '/communities',        key: 'communities' },
      { path: '/transformations',    key: 'transformations' },
      { path: '/domains',            key: 'domains' },
      { path: '/domains?view=cards', key: 'cardLibrary', sub: true },
    ],
  },
  {
    key: 'rulesAndMechanics',
    items: [
      { path: '/mechanics',  key: 'coreMechanics' },
      { path: '/equipment',  key: 'equipment' },
      { path: '/items',      key: 'itemsConsumables' },
      { path: '/beastforms', key: 'beastforms' },
    ],
  },
  {
    key: 'gameMaster',
    items: [
      { path: '/gm-tools',       key: 'gmGuidance' },
      { path: '/adversaries',    key: 'adversaries' },
      { path: '/environments',   key: 'environments' },
      { path: '/campaign-frame', key: 'witherwildFrame' },
      { path: '/kohd',           key: 'kohd', tech: true },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const s = useStrings();
  const n = s.nav;
  const { isNew } = useVoidNew();

  return (
    <>
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />
      )}

      <nav className={`sidebar ${isOpen ? 'open' : ''}`} aria-label={n.ariaLabel}>
        <div className="sidebar-logo">
          <Link to="/" className="sidebar-logo-link" onClick={onClose}>
            <h1>{n.logo.title}</h1>
            <p>{n.logo.subtitle}</p>
          </Link>
        </div>

        <button className="sidebar-close-btn" onClick={onClose} aria-label={n.closeAria}>
          <X size={16} />
        </button>

        <div className="sidebar-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.key} className="nav-group">
              <div className="nav-group-label">{n.groups[group.key]}</div>
              {group.items.map((item) => {
                const label = n.items[item.key];
                const isActive = item.sub
                  ? location.pathname + location.search === item.path
                  : location.pathname === item.path && !location.search.includes('view=cards');

                if (item.sub) {
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-sub-item ${isActive ? 'active' : ''}`}
                      onClick={onClose}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className="nav-sub-icon" aria-hidden="true" />
                      {label}
                    </Link>
                  );
                }

                const showNew = NEW_PATHS_SET.has(item.path) && isNew(item.path);
                if (item.tech || item.featured) {
                  const Icon = item.tech ? CircuitBoard : UserRoundPlus;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-item nav-item--${item.tech ? 'tech' : 'featured'}${item.beta ? ' nav-item--beta' : ''} ${isActive ? 'active' : ''}`}
                      onClick={onClose}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon size={14} className="nav-tool-icon" aria-hidden="true" />
                      <span className="nav-tool-label">{label}</span>
                      {showNew && <span className="nav-new-chip">{n.newBadge}</span>}
                      {item.beta && <span className="beta-chip">{n.betaBadge}</span>}
                    </Link>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    onClick={onClose}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {label}
                    {showNew && <span className="new-star">*</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
